import React, { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { paperSx } from "./PlaceAutocomplete";
import mark from "../imgs/wandr-mark.svg";
import { matchesSearch } from "../placeSearch";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAX_APP_RESULTS = 5;

const GROUP_APP = "Alojamientos en Wandr";
const GROUP_GOOGLE = "Direcciones y ciudades";

/** Wandr listings matching the text, by the same rules as the app's other search boxes. */
const matchAppPlaces = (places, query) => {
  if (!query.trim()) return [];
  return places
    .filter((p) => matchesSearch(p, query))
    .slice(0, MAX_APP_RESULTS)
    .map((p) => ({
      group: GROUP_APP,
      id: `app-${p.id}`,
      main: p.title || p.name || "Alojamiento",
      secondary: [p.address, p.city, p.country].filter(Boolean).join(", "),
      place: p,
    }));
};

/** Google address/city suggestions for `query`; empty without the Places library. */
const useGoogleSuggestions = (query, skip) => {
  const placesLib = useMapsLibrary("places");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // One session token spans the suggestions and the final details lookup, which Google bills as one session.
  const sessionToken = useRef(null);

  useEffect(() => {
    const text = query.trim();
    if (!placesLib || text.length < 2 || skip) {
      setOptions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!sessionToken.current) sessionToken.current = new placesLib.AutocompleteSessionToken();
      setLoading(true);
      try {
        const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text,
          sessionToken: sessionToken.current,
          language: "es",
        });
        if (cancelled) return;
        setOptions(
          suggestions
            .map((s) => s.placePrediction)
            .filter(Boolean)
            .map((p) => ({
              group: GROUP_GOOGLE,
              id: `google-${p.placeId}`,
              main: p.mainText?.text || p.text.text,
              secondary: p.secondaryText?.text || "",
              prediction: p,
            }))
        );
      } catch (error) {
        console.error("Error buscando sugerencias:", error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, placesLib, skip]);

  const endSession = () => {
    sessionToken.current = null;
  };

  return { options, loading, endSession };
};

const SearchField = ({ places, onSelect, sx, useGoogle }) => {
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState(null);
  // Picking a suggestion rewrites the text; don't search again for that text.
  const picked = value && inputValue === value.main;
  const google = useGoogle(inputValue, picked);

  const appOptions = useMemo(
    () => (picked ? [] : matchAppPlaces(places, inputValue)),
    [places, inputValue, picked]
  );
  const options = [...appOptions, ...google.options];

  const handleChange = async (_, option) => {
    if (!option || typeof option === "string") return;
    setValue(option);
    setInputValue(option.main);

    if (option.place) {
      onSelect({ placeId: option.place.id });
      return;
    }
    try {
      const place = option.prediction.toPlace();
      await place.fetchFields({ fields: ["location", "viewport"] });
      onSelect({ location: place.location?.toJSON(), viewport: place.viewport?.toJSON() });
    } catch (error) {
      console.error("Error obteniendo el lugar:", error);
    } finally {
      google.endSession();
    }
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      options={options}
      value={value}
      groupBy={(option) => option.group}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.main)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={inputValue}
      onInputChange={(_, text, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(text);
        if (reason === "clear") setValue(null);
      }}
      onChange={handleChange}
      loading={google.loading}
      slotProps={{ paper: { sx: paperSx } }}
      sx={sx}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.place ? (
            <Box component="img" src={mark} alt="" sx={{ width: 20, height: 20, mr: 1.5 }} />
          ) : (
            <PlaceIcon sx={{ mr: 1.5, color: "#2dd4bf", fontSize: 20 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {option.main}
            </Typography>
            {option.secondary && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {option.secondary}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Buscar alojamiento, dirección o ciudad..."
          // Keep the browser's own address autofill from covering the suggestions.
          inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {google.loading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const noGoogle = () => ({ options: [], loading: false, endSession: () => {} });

/**
 * Home search box: suggests Wandr listings (by name or address) and Google addresses/cities.
 * `onSelect` gets `{ placeId }` for a listing or `{ location, viewport }` for a Google place.
 */
const HomeSearch = (props) => {
  if (!API_KEY) return <SearchField {...props} useGoogle={noGoogle} />;
  return (
    <APIProvider apiKey={API_KEY} language="es">
      <SearchField {...props} useGoogle={useGoogleSuggestions} />
    </APIProvider>
  );
};

export default HomeSearch;
